/**
 * The bridge between `datetime-local` inputs and the API.
 *
 * The input speaks the viewer's wall clock and carries no offset, while every
 * timestamp the API accepts is ISO-8601 with one. Both conversions live here so
 * the create and reschedule forms cannot drift apart on what "18:00" means.
 */

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function toInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** An ISO instant into the local `YYYY-MM-DDTHH:mm` the input expects. */
export function toDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : toInputValue(date);
}

/** The next :00 or :30, which is what interviews are almost always booked on. */
export function nextHalfHour(): string {
  const date = new Date();
  date.setSeconds(0, 0);
  // setMinutes(60) rolls the hour over, so the top of the next hour is free.
  date.setMinutes(date.getMinutes() < 30 ? 30 : 60);
  return toInputValue(date);
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Start of a `YYYY-MM-DD` in the viewer's timezone, as an ISO instant. */
export function startOfDayIso(day: string): string | null {
  const match = DAY.exec(day);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** End of a `YYYY-MM-DD` in the viewer's timezone, as an ISO instant. */
export function endOfDayIso(day: string): string | null {
  const match = DAY.exec(day);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** `null` for an unparseable value so callers can report it as one field error. */
export function toOffsetIso(local: string): string | null {
  if (local === '') return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
